import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { INITIAL_PAGINATED_RESPONSE } from '../core/constants/table-columns.const';
import { TableParams } from '../shared/components/table/models/page.model';
import { TransactionState } from './transaction.model';
import { inject } from '@angular/core';
import { TransactionService } from '../core/services/transaction.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, map, pipe, switchMap, tap } from 'rxjs';
import { AssociationTypesEnum } from '../core/enums/association-types.enum';
import { SharedDataService } from '../core/services/shared-data.service';
import { Role } from './shared.model';
import { tapResponse } from '@ngrx/operators';
import { CachedTransactionFilterEntry, Transaction, TransactionFiltersPayload } from '../core/models/transaction.model';
import saveAs from 'file-saver';
import { UtilityService } from '../core/services/utility.service';
import { ToastService } from '../core/services/toast.service';
import { TransactionRoleTypesEnum } from '../core/enums/transaction-role-types.enum';

const initialState: TransactionState = {
  roles: [],
  associationValue: [],
  paginatedTransactionList: INITIAL_PAGINATED_RESPONSE(),
  cachedTransactionFilters: null,
  selectedTransactions: [],
  isTransactionTableLoading: false,
  isTransactionListInitialized: false,
  isRolesInitialized: false,
  isTransactionInfoDownLoading: false,
  isQuotaUsageDownLoading: false,
  isChainExportDownLoading: false,
  isTransactionDeleting: false,
  isTransactionDeleteDone: false,
  isTransactionFiltersLoading: false,
  refreshTransactionTable: false,
  isProjectAdmin: false,
  transactionActiveFilters: {},
  multiTierMode: false,
  multiTierFinalFilter: true
};

let tableParams: TableParams = { page: 1, pageSize: 10 };

export const TransactionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      transactionService = inject(TransactionService),
      sharedDataService = inject(SharedDataService),
      utilityService = inject(UtilityService),
      toastService = inject(ToastService)
    ) => ({
      getRoles: rxMethod<void>(
        pipe(
          switchMap(() =>
            sharedDataService.getRoles(AssociationTypesEnum.PROJECT_CODE).pipe(
              catchError((err) => {
                console.error(err);
                patchState(store, { isRolesInitialized: true });
                return EMPTY;
              })
            )
          ),
          tapResponse({
            next: (roles: Role[]) => {
              patchState(store, {
                roles: roles,
                isRolesInitialized: true,
                associationValue: roles ? roles.map((role) => role.associationValue) : [],
                isProjectAdmin: roles.some((role) => role.name.toUpperCase() === TransactionRoleTypesEnum.PROJECT_ADMIN)
              });
            },
            error: () => {}
          })
        )
      ),
      getPaginatedTransactionList: rxMethod<{ params: TableParams }>(
        pipe(
          tap(({ params }) => {
            tableParams = params;
          }),
          switchMap(() => {
            // Direct tab = plantation-direct only (own plantations, no upstream chain) so multi-tier
            // finals don't leak in; Multi-tier tab = multiTierOnly (+ finalTierOnly).
            const tierFlags = store.multiTierMode()
              ? { multiTierOnly: true, ...(store.multiTierFinalFilter() ? { finalTierOnly: true } : {}) }
              : { directPlantationOnly: true };
            return transactionService.getPaginatedTransactionList({
              page: tableParams.page,
              pageSize: tableParams.pageSize!,
              sort: tableParams?.sort,
              filters: {
                projectCode: store.associationValue(),
                ...tierFlags,
                ...tableParams?.filters
              }
            });
          }),
          tapResponse({
            next: (res) => {
              patchState(store, {
                paginatedTransactionList: res,
                isTransactionListInitialized: true,
                isTransactionTableLoading: false,
                transactionActiveFilters: { sort: tableParams?.sort, filters: tableParams?.filters }
              });
            },
            error: () => {}
          })
        )
      ),
      exportTransactions: rxMethod<{ transactionIds: number[] }>(
        pipe(
          tap(() => {
            patchState(store, { isTransactionInfoDownLoading: true });
          }),
          switchMap(({ transactionIds }) => {
            // Scope the export to the active tab, mirroring the list.
            const tierFlags = store.multiTierMode() ? { multiTierOnly: true } : { directPlantationOnly: true };
            return transactionService.exportTransactions({
              transactionIds: transactionIds,
              projectCode: store.associationValue(),
              activeTableFilters: store.transactionActiveFilters(),
              ...tierFlags
            });
          }),
          tapResponse({
            next: (file) => {
              saveAs(file.body as Blob, utilityService.getFileNameFromHeaders(file));
              patchState(store, { isTransactionInfoDownLoading: false });
            },
            error: () => {
              patchState(store, { isTransactionInfoDownLoading: false });
            }
          })
        )
      ),
      // Mode 2 — full upstream chain for a final transaction (the per-row action download).
      exportTransactionChains: rxMethod<{ transactionIds: number[] }>(
        pipe(
          tap(() => {
            patchState(store, { isChainExportDownLoading: true });
          }),
          switchMap(({ transactionIds }) => transactionService.exportTransactionChains(transactionIds)),
          tapResponse({
            next: (file) => {
              saveAs(file.body as Blob, utilityService.getFileNameFromHeaders(file));
              patchState(store, { isChainExportDownLoading: false });
            },
            error: () => {
              patchState(store, { isChainExportDownLoading: false });
            }
          })
        )
      ),
      exportQuotaUsage: rxMethod<void>(
        pipe(
          tap(() => {
            patchState(store, { isQuotaUsageDownLoading: true });
          }),
          switchMap(() => {
            return transactionService.exportQuotaUsage({
              projectCode: store.associationValue(),
              activeTableFilters: store.transactionActiveFilters()
            });
          }),
          tapResponse({
            next: (file) => {
              saveAs(file.body as Blob, utilityService.getFileNameFromHeaders(file));
              patchState(store, { isQuotaUsageDownLoading: false });
            },
            error: () => {
              patchState(store, { isQuotaUsageDownLoading: false });
            }
          })
        )
      ),
      deleteTransactions: rxMethod<{ transactionIds: number[] }>(
        pipe(
          tap(() => {
            patchState(store, { isTransactionDeleting: true });
          }),
          switchMap(({ transactionIds }) => transactionService.deleteTransactions(transactionIds)),
          tapResponse({
            next: (res) => {
              // TODO: handle array of emit toasts. need to config in ARP repo.
              toastService.emitToast({
                isSuccess: res[0].status.toLowerCase() === 'success',
                message: res[0]?.message
              });
              patchState(store, {
                isTransactionDeleting: false,
                isTransactionDeleteDone: true,
                refreshTransactionTable: true,
                isTransactionTableLoading: true,
                selectedTransactions: []
              });

              setTimeout(() => {
                patchState(store, { isTransactionDeleteDone: false, refreshTransactionTable: false });
              }, 500);
            },
            error: () => {}
          })
        )
      ),
      getPaginatedTransactionFilters: rxMethod<{ params: TransactionFiltersPayload }>(
        pipe(
          tap(() => {
            patchState(store, { isTransactionFiltersLoading: true });
          }),

          switchMap(({ params }) => {
            const tierFlags = store.multiTierMode() ? { multiTierOnly: true } : { directPlantationOnly: true };
            return transactionService
              .getPaginatedTransactionFilters({ ...params, ...tierFlags, projectCode: store.associationValue() })
              .pipe(map((res) => ({ res, params })));
          }),

          tapResponse({
            next: ({ res, params }) => {
              const currentCache = store.cachedTransactionFilters();
              const currentField = params.field;
              const fieldCache = currentCache?.[currentField];

              const existingValueSet = new Set((fieldCache?.items ?? []).map((i) => JSON.stringify(i.value)));

              const newItems = res.results.filter((item) => !existingValueSet.has(JSON.stringify(item.value)));

              const mergedItems = [...(fieldCache?.items ?? []), ...newItems];

              const loadedPages = new Set(fieldCache?.loadedPages ?? []);
              loadedPages.add(res.page);

              const updatedFieldCache: CachedTransactionFilterEntry = {
                items: mergedItems,
                count: res.count,
                pageSize: res.pageSize,
                numPages: res.numPages,
                loadedPages
              };

              patchState(store, {
                cachedTransactionFilters: {
                  ...currentCache,
                  [currentField]: updatedFieldCache
                },
                isTransactionFiltersLoading: false
              });
            },

            error: () => {
              patchState(store, { isTransactionFiltersLoading: false });
            }
          })
        )
      ),
      clearCachedTransactionFilters(): void {
        patchState(store, { cachedTransactionFilters: null });
      },
      setTransactionTableLoading(isLoading: boolean): void {
        patchState(store, { isTransactionTableLoading: isLoading });
      },
      setMultiTierMode(multiTierMode: boolean): void {
        patchState(store, { multiTierMode });
      },
      setMultiTierFinalFilter(multiTierFinalFilter: boolean): void {
        patchState(store, { multiTierFinalFilter });
      },
      setTransactionTableSelections(transactions: Transaction[]): void {
        patchState(store, { selectedTransactions: transactions });
      },
      resetLoadingStates(initializeRoles = false): void {
        patchState(store, {
          isRolesInitialized: initializeRoles,
          isTransactionListInitialized: true,
          isTransactionInfoDownLoading: false,
          isQuotaUsageDownLoading: false,
          isChainExportDownLoading: false,
          isTransactionTableLoading: false,
          isTransactionDeleting: false,
          isTransactionFiltersLoading: false
        });
      }
    })
  )
);
